import { AfterViewInit, Directive, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, } from '@angular/core';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import panzoom, { PanZoom } from 'panzoom';
import { MAX_PERCENT } from "../models/consts";

const ZOOM_SPEED_FACTOR = 0.08;
const ZOOM_IN_FACTOR = 1.08;
const ZOOM_OUT_FACTOR = 0.92;
const MAX_ZOOM_LEVEL = 2.5;
const BASE_ZOOM_LEVEL = 1;
const ZOOM_CONTAINER_BOUNDS_PADDING = 1;

@Directive({
  selector: '[appZoom]',
  standalone: false,
})
export class ZoomDirective implements AfterViewInit, OnDestroy {
  @Input() zoomIn$: Observable<void>|undefined;
  @Input() zoomOut$: Observable<void>|undefined;
  @Input() disableZoom$: Observable<boolean>|undefined;
  @Input() reset$: Observable<void>|undefined;
  @Output() zoomChange = new EventEmitter<number>();
  @Output() panzoomActive = new EventEmitter<boolean>();
  @Output() boundsReached = new EventEmitter<boolean>();
  private panzoom: PanZoom|undefined;
  private startPanPoint = { x: 0, y: 0 };
  private readonly subscription = new Subscription();

  constructor(private hostRef: ElementRef, private ngZone: NgZone) {
    this.hostRef.nativeElement.addEventListener('mousedown', () => this.onMouseDown());
  }

  get panzoomScale(): number|undefined {
    return this.panzoom ? Math.round(this.panzoom.getTransform().scale * MAX_PERCENT) : undefined;
  }

  ngAfterViewInit(): void {
    this.panzoom = panzoom(this.hostRef.nativeElement, {
      maxZoom: MAX_ZOOM_LEVEL,
      minZoom: BASE_ZOOM_LEVEL,
      zoomSpeed: ZOOM_SPEED_FACTOR,
      disableKeyboardInteraction: true,
      bounds: true,
      boundsPadding: ZOOM_CONTAINER_BOUNDS_PADDING,
      smoothScroll: false,
      zoomDoubleClickSpeed: BASE_ZOOM_LEVEL,
      filterKey: () => true,
      onDoubleClick: () => {
        return 0;
      },
      onTouch: () => {
        return 0;
      },
      beforeMouseDown: () => {
        return 0;
      },
      onClick: () => {
        return 0;
      },
    });


    this.ngZone.runOutsideAngular(() => {
      this.subscription.add(
        fromEvent(window, 'resize')
          .pipe(debounceTime(50))
          .subscribe(() => this.resetPanZoom())
      );
    });

    this.panzoom?.on('zoom', () => this.onPanZoom());
    this.panzoom?.on('panstart', (event: PanZoom) => this.onPanStart(event));
    this.panzoom?.on('pan', (event: PanZoom) => this.onPan(event));

    this.subscription.add(
      this.disableZoom$?.subscribe((isDisabled) => {
        isDisabled ? this.panzoom?.pause() : this.panzoom?.resume();
      })
    );

    this.subscription.add(
      this.zoomIn$?.subscribe(() => {
        const originX = window.innerWidth / 2;
        const originY = window.innerHeight / 2;
        this.panzoom?.smoothZoom(originX, originY, ZOOM_IN_FACTOR);
        this.shouldReset();
      })
    );

    this.subscription.add(
      this.zoomOut$?.subscribe(() => {
        const originX = window.innerWidth / 2;
        const originY = window.innerHeight / 2;
        this.panzoom?.smoothZoom(originX, originY, ZOOM_OUT_FACTOR);
        this.shouldReset();
      })
    );

    this.subscription.add(
      this.reset$?.subscribe(() => {
        this.resetPanZoom();
      })
    );
  }

  private onMouseDown(): void {
    this.panzoomActive.emit(false);
  }

  private onPanStart(event: PanZoom): void {
    const { x, y, scale } = event.getTransform();
    this.startPanPoint = { x, y };
    if (scale === BASE_ZOOM_LEVEL && (x !== 0 || y !== 0)) {
      this.panzoom?.zoomAbs(0, 0, BASE_ZOOM_LEVEL);
    }
  }

  private onPan(event: PanZoom): void {
    const x = Math.abs(Math.round(event.getTransform().x));
    const startX = Math.round(Math.abs(this.startPanPoint.x));

    if (startX === x) {
      this.panzoomActive.emit(false);
    } else {
      this.panzoomActive.emit(true);
    }

    this.boundsReached.emit(startX !== x ? false : true);
  }

  private onPanZoom(): void {
    this.zoomChange.emit(this.panzoomScale);
    this.panzoomActive.emit(true);
  }

  private shouldReset(): void {
    if (this.panzoomScale && this.panzoomScale === MAX_PERCENT) {
      this.resetPanZoom();
    }
  }

  private resetPanZoom(): void {
    this.panzoom?.zoomAbs(0, 0, BASE_ZOOM_LEVEL);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.panzoom?.dispose();
    this.hostRef.nativeElement.removeEventListener('mousedown', this.onMouseDown());
  }
}
